/**
 * UI projection of internal-token cost from the public billing configs
 * (`GET /content-master/workspace-config.publicConfigs`) - the same formula
 * the server pre-flight uses (see OpenAPI on `POST /bulk/generate`):
 *
 *   price_per_operation = ceil((base + fieldsCount × perField) × tierMultiplier)
 *
 * `base`/`perField` are the generate or translate config pair. The tier
 * multiplier is hardcoded to tier 1 (current backend model gpt-4o-mini);
 * keep in sync when the backend model changes.
 *
 * The estimate is informational - the authoritative check is the server
 * pre-flight and the per-product re-check in the worker.
 */
import { ConfigView } from '../api/generated/models/ConfigView';

export type ContentMasterTokenEstimateKind = 'generate' | 'translate';

const CONFIG_KEYS: Record<
  ContentMasterTokenEstimateKind,
  { base: ConfigView.name; perField: ConfigView.name }
> = {
  generate: {
    base: ConfigView.name.CONTENT_MASTER_BASE_TOKENS_GENERATE,
    perField: ConfigView.name.CONTENT_MASTER_TOKENS_PER_FIELD_GENERATE,
  },
  translate: {
    base: ConfigView.name.CONTENT_MASTER_BASE_TOKENS_TRANSLATE,
    perField: ConfigView.name.CONTENT_MASTER_TOKENS_PER_FIELD_TRANSLATE,
  },
};

const readNumericConfig = (
  publicConfigs: ConfigView[] | null | undefined,
  name: ConfigView.name
): number | null => {
  const raw = publicConfigs?.find((c) => c.name === name)?.value;
  const n = raw == null ? NaN : Number(raw);
  return Number.isFinite(n) ? n : null;
};

/**
 * Estimated internal-token cost of `operationCount` operations, each
 * generating/translating `fieldsCount` fields. Pass `fieldsCount = 0` when the
 * field count is unknown client-side (CM bulk - the worker resolves the field
 * list from Wawi) to get the base-rate lower bound. Returns `null` when the
 * required config keys are missing (the UI hides/greys the estimate row).
 */
export function estimateContentMasterAiTokens(
  publicConfigs: ConfigView[] | null | undefined,
  fieldsCount: number,
  operationCount: number,
  kind: ContentMasterTokenEstimateKind
): number | null {
  if (operationCount <= 0) {
    return 0;
  }
  const keys = CONFIG_KEYS[kind];
  const base = readNumericConfig(publicConfigs, keys.base);
  const perField = readNumericConfig(publicConfigs, keys.perField);
  if (base == null || perField == null) {
    return null;
  }
  const tierMultiplier =
    readNumericConfig(publicConfigs, ConfigView.name.CONTENT_MASTER_MODEL_TIER_1_MULTIPLIER) ?? 1;
  return Math.ceil((base + fieldsCount * perField) * tierMultiplier) * operationCount;
}

/**
 * Token surcharge for the user-provided `additionalContext` of a SINGLE
 * generation - same formula as the server pre-flight:
 *
 *   ceil( chars / 10000 × RATE × ceil(fieldsCount / 10) )
 *
 * `chars` is the length of the SENT context string (after trim); `RATE` comes
 * from the `content_master_tokens_per_10k_context_chars` public config.
 * `ceil(fieldsCount / 10)`: attributes are generated in chunks of 10 and the
 * context is re-sent with every chunk. Returns 0 for an empty context / no
 * fields, `null` when the config key is missing (hide the surcharge row).
 */
export function estimateAdditionalContextTokens(
  publicConfigs: ConfigView[] | null | undefined,
  contextChars: number,
  fieldsCount: number
): number | null {
  if (contextChars <= 0 || fieldsCount <= 0) {
    return 0;
  }
  const rate = readNumericConfig(
    publicConfigs,
    ConfigView.name.CONTENT_MASTER_TOKENS_PER_10K_CONTEXT_CHARS
  );
  if (rate == null) {
    return null;
  }
  return Math.ceil((contextChars / 10000) * rate * Math.ceil(fieldsCount / 10));
}
